import { type Message } from '@ag-ui/core';
import { type StateCreator } from 'zustand';

import { type CanvasTokenNode } from './createCanvasTokenNodeSlice';

/** Typed agent state returned by the AG-UI agent. */
export type AgentState =
  | ({
      mediaMap?: Record<string, CanvasTokenNode>;
      sessionSummary?: string;
      sessionTitle?: string;
      workspaceId?: string;
    } & Record<string, unknown>)
  | null;

export type ThreadData = {
  messages: Message[];
  state: AgentState;
};

export type ChatSlice = {
  // State — runtime cache for agent messages/state per thread
  threads: Record<string, ThreadData>;

  // Thread-specific operations
  setMessagesForThread: (threadId: string, messages: Message[]) => void;
  setStateForThread: (threadId: string, state: AgentState) => void;
};

const createEmptyThread = (): ThreadData => ({
  messages: [],
  state: null,
});

export type CreateChatSlice = (
  initialState?: Partial<Pick<ChatSlice, 'threads'>>,
) => StateCreator<ChatSlice, [], [], ChatSlice>;

export const createChatSlice: CreateChatSlice =
  (initialState = {}) =>
  (set) => ({
    setMessagesForThread: (threadId, messages) => {
      set((state) => {
        const thread = state.threads[threadId];

        return {
          threads: {
            ...state.threads,
            [threadId]: {
              ...(thread ?? createEmptyThread()),
              // Spread to create new array reference - ag-ui client may pass same mutated array
              messages: [...messages],
            },
          },
        };
      });
    },

    setStateForThread: (threadId, newState) => {
      set((state) => {
        const thread = state.threads[threadId];

        return {
          threads: {
            ...state.threads,
            [threadId]: {
              ...(thread ?? createEmptyThread()),
              state: newState,
            },
          },
        };
      });
    },

    threads: initialState.threads ?? {},
  });
